"use client";
import { useEffect, useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useSiteData } from "@/contexts/SiteDataContext";
import { fetchWithCache } from "@/lib/server-utils";
import { processImageUrl } from "../lib/imageUrl";
import { request, getLangFromPath } from "@/lib/request";
gsap.registerPlugin(ScrollTrigger);

interface Faq {
  title: string;
  face: string;
  content: string;
  id: number;
  createtime: string;
  desc: string;
}

interface FaqItemProps {
  question: string;
  answer: string;
  index: number;
  icon: string;
  isOpen: boolean;
  onToggle: () => void;
}

// 箭头图片组件（始终向下，通过父容器旋转控制方向）
const ArrowIcon = ({ className = "", isOpen = false }: { className?: string; isOpen?: boolean }) => (
  <img
    className={className}
    src="https://oss.yy-aicloud.com/uploads/20261014/6acf390559922282bb8a7afdb969e5ac8e1848f741d9a.png"
    width="24"
    height="24"
    alt="arrow"
    style={{ 
      display: "block",
      filter: isOpen ? "brightness(0) invert(1)" : "none",
      transition: "filter 0.3s ease",
    }}
  />
);

// 单个 FAQ 项组件
function FaqItem({ question, answer, index, icon, isOpen, onToggle }: FaqItemProps) {
  const contentRef = useRef<HTMLDivElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const contentInnerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (contentRef.current && contentInnerRef.current) {
      if (isOpen) {
        const contentHeight = contentInnerRef.current.scrollHeight;
        gsap.to(contentRef.current, {
          maxHeight: contentHeight + 40,
          opacity: 1,
          paddingTop: 0,
          paddingBottom: "20px",
          duration: 0.4,
          ease: "power2.out",
          onComplete: () => {
            if (contentRef.current) {
              gsap.set(contentRef.current, { maxHeight: "9999px" });
            }
            ScrollTrigger.refresh();
            void document.body.offsetHeight;
          },
        });
      } else {
        gsap.to(contentRef.current, {
          maxHeight: 0,
          opacity: 0,
          paddingTop: 0,
          paddingBottom: 0,
          duration: 0.4,
          ease: "power2.out",
          onComplete: () => {
            ScrollTrigger.refresh();
            void document.body.offsetHeight;
          },
        });
      }
    }

    if (iconRef.current) {
      gsap.to(iconRef.current, {
        rotation: isOpen ? 180 : 0,
        duration: 0.3,
        ease: "power2.out",
      });
    }
  }, [isOpen]);

  return (
    <div
      className={`faq-item width100 ${isOpen ? "active1" : ""}`}
      style={{
        marginTop: "16px",
        background: isOpen ? "#0A5694" : "#fff",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "background 0.3s ease, box-shadow 0.3s ease",
        boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
      }}
    >
      <div
        className="faq-header width100 pointer"
        onClick={onToggle}
        style={{
          padding: "20px 24px",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          transition: "color 0.3s ease",
          borderBottom: "none",
        }}
      >
        <span
          className="faq-title"
          style={{
            flex: "1 1 0",
            minWidth: 0,
            overflowWrap: "break-word",
            wordBreak: "break-word",
            color: isOpen ? "#fff" : "#1a1a1a",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          {question}
        </span>

        <div
          ref={iconRef}
          className="faq-toggle-icon"
          style={{
            width: "32px",
            height: "32px",
            borderRadius: "50%",
            background: isOpen ? "#1489F2" : "#f5f5f7",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginLeft: "16px",
            flexShrink: 0,
            transition: "all 0.3s ease",
          }}
        >
          <ArrowIcon isOpen={isOpen} />
        </div>
      </div>

      <div
        ref={contentRef}
        className="faq-content c6 width100"
        style={{
          overflow: "hidden",
          paddingLeft: "24px",
          paddingRight: "24px",
          boxSizing: "border-box",
          height: "auto",
          borderBottom: "none",
        }}
      >
        <div ref={contentInnerRef} style={{ display: "flex" }}>
          <div
            className="faq-answer-html"
            style={{
              width: "100%",
              lineHeight: "1.6",
              color: "#fff",
              fontSize: "14px",
            }}
            dangerouslySetInnerHTML={{ __html: answer }}
          />
        </div>
      </div>
    </div>
  );
}

interface Props {
  cateUniq?: string;
  limit?: number;
  page?: number;
  title?: string;
  data?: any[];
}

export default function Faq({ cateUniq, limit, page, title, data }: Props) {
  const { settingData, faqData } = useSiteData();
  const [isLoading, setIsLoading] = useState(false);
  const [customFaqData, setCustomFaqData] = useState<any[]>([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const sectionRef = useRef<HTMLElement>(null);

  let faqItems: any[];
  if (Array.isArray(data) && data.length > 0) {
    faqItems = data;
  } else if (data !== undefined) {
    faqItems = data;
  } else if (cateUniq || limit || page) {
    faqItems = customFaqData;
  } else {
    faqItems = Array.isArray(faqData) ? faqData : [];
  }

  useEffect(() => {
    if (data !== undefined) return;
    if (cateUniq || limit || page) {
      const fetchCustomFaqData = async () => {
        setIsLoading(true);
        try {
          const lang = getLangFromPath();
          const params: any = {};
          if (cateUniq) params.cateUniq = cateUniq;
          if (limit) params.limit = limit;
          if (page) params.page = page;
          if (lang) params.lang = lang;

          const result = await fetchWithCache("/api/faq", params);
          const data = result?.data;
          setCustomFaqData(
            Array.isArray(data) ? data : Object.values(data || {}),
          );
        } catch (error) {
          console.error("Error fetching custom FAQ data:", error);
          setCustomFaqData([]);
        } finally {
          setIsLoading(false);
        }
      };

      fetchCustomFaqData();
    }
  }, [cateUniq, limit, page, data]);

  useEffect(() => {
    if (!sectionRef.current) return;
    const ctx = gsap.context(() => {
      gsap.from(".faq-title-animate", {
        opacity: 0,
        duration: 0.8,
        stagger: 0.1,
        ease: "power3.out",
        scrollTrigger: {
          trigger: sectionRef.current,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      });

      gsap.from(".faq-item", {
        opacity: 0,
        duration: 0.6,
        stagger: 0.15,
        ease: "power2.out",
        scrollTrigger: {
          trigger: ".faq-list",
          start: "top 85%",
          toggleActions: "play none none reverse",
        },
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [faqItems]);

  const iconUrl =
    "https://oss.yy-aicloud.com/uploads/69af85e4426d60e4e946668cf2afc4299b462b812caca.png";

  const fallbackData = [
    {
      id: 1,
      title: "Is The Initial Consultation Free Of Charge?",
      content:
        "For international B2B procurement and distributors, our standard Minimum Order Quantity (MOQ) is typically set at one 40'HC shipping container. This ensures the unit economics and cross-border freight costs remain highly competitive.",
    },
    {
      id: 2,
      title: "What Documentation Is Required For The Consultation?",
      content:
        "We typically require your company profile, project specifications, and intended application details to provide accurate consultation and quotation.",
    },
    {
      id: 3,
      title: "What Is The Typical Lead Time For A 40'HC Container?",
      content:
        "Standard lead time ranges from 4-6 weeks depending on product configuration and current production schedule.",
    },
    {
      id: 4,
      title: "How Do Your Crates Compare To Traditional Concrete Systems?",
      content:
        "Our modular crate systems offer superior flexibility, faster installation, and comparable structural integrity at a fraction of the weight.",
    },
  ];

  const displayData =
    Array.isArray(faqItems) && faqItems.length > 0 ? faqItems : fallbackData;

  return (
    <section
      id="faq"
      ref={sectionRef}
      className="faq-section"
      style={{
        background: "#f5f5f7",
        height: "auto",
        minHeight: "0 !important",
        padding: "0",
      }}
    >
      <div
        className="faq-container"
        style={{ height: "auto", minHeight: "0 !important", maxWidth: "1400px", margin: "0 auto", padding: "60px 20px" }}
      >
        <div
          className="width100 faq-content faq-clearfix"
          style={{ height: "auto" }}
        >
          <div
            className="width100 faq-title-animate"
            style={{ marginBottom: "40px", textAlign: "center" }}
          >
            <h2 className="faq-main-title-text" style={{ fontSize: "36px", fontWeight: 700, color: "#1a1a1a", margin: 0 }}>
              {settingData?.faqJson?.["item_1774599089570_u5ugqh8ce"]?.value ||
                "FAQ"}
            </h2>
          </div>

          <div
            className="faq-list width100 faq-clearfix"
            style={{ height: "auto" }}
          >
            {isLoading ? (
              <div className="textcenter" style={{ padding: "2rem" }}>
                <div className="f2">Loading...</div>
              </div>
            ) : (
              displayData.map((item, index) => (
                <FaqItem
                  key={item.uniq || index}
                  index={index}
                  question={item.title || ""}
                  answer={item.content || ""}
                  icon={iconUrl}
                  isOpen={activeIndex === index}
                  onToggle={() => setActiveIndex(activeIndex === index ? -1 : index)}
                />
              ))
            )}
          </div>
        </div>
      </div>

      <style>{`
        .faq-clearfix::after {
          content: "";
          display: block;
          clear: both;
          height: 0;
          visibility: hidden;
        }

        .faq-item:hover {
          box-shadow: 0 4px 16px rgba(0,0,0,0.08);
        }
        .faq-toggle-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          flex-shrink: 0;
        }
        .faq-toggle-icon img {
          transform-origin: center center;
          will-change: transform;
        }
        .faq-answer-html p {
          margin: 0 0 10px 0;
        }
        .faq-answer-html p:last-child {
          margin-bottom: 0;
        }
        .faq-answer-html a {
          color: #fff;
          text-decoration: underline;
        }
        .faq-answer-html ul,
        .faq-answer-html ol {
          margin: 10px 0;
          padding-left: 20px;
        }
        .faq-answer-html li {
          margin-bottom: 5px;
        }
        .faq-answer-html strong {
          font-weight: 600;
        }
        .faq-answer-html img {
          max-width: 100%;
          height: auto;
        }

        .faq-section,
        .faq-container,
        .faq-content,
        .faq-list {
          min-height: 0 !important;
          height: auto !important;
          box-sizing: border-box;
        }

        @media screen and (max-width: 767px) {
          .faq-container {
            padding: 40px 16px;
          }
          .faq-main-title-text {
            font-size: 28px !important;
          }
          .faq-item {
            margin-top: 12px !important;
          }
          .faq-header {
            padding: 16px 20px !important;
          }
          .faq-title {
            font-size: 14px !important;
          }
          .faq-toggle-icon {
            width: 28px !important;
            height: 28px !important;
          }
          .faq-answer-html {
            font-size: 13px !important;
          }
        }

        @media screen and (max-width: 480px) {
          .faq-main-title-text {
            font-size: 24px !important;
          }
          .faq-header {
            padding: 14px 16px !important;
          }
          .faq-title {
            font-size: 13px !important;
          }
        }
      `}</style>
    </section>
  );
}